import { Observation, QuestionnaireResponse } from "@medplum/fhirtypes";

import { DEFAULT_ENCOUNTER_AMEND_BOT_LAG_MS } from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import {
  getObservationPreviousVersionTargetId,
  resolveObservationAmendPredecessorId,
} from "@/encounters/components/EncounterLabor/utils/observation-version-chain";
import {
  getObservationEncounterAmendParentVersionId,
  getQuestionnaireResponseEncounterParentVersionId,
} from "@/encounters/utils/questionnaire-response-amend-stamp";
import {
  getQuestionnaireResponseChangeInstant,
  parseIsoInstant,
} from "@/encounters/utils/questionnaire-response-instant";
import { getQuestionnaireResponseIdFromObservationDerivedFrom } from "@/utils/medplum";

export interface EncounterAmendmentObservationFilterOptions {
  /** Slack after the newer encounter QuestionnaireResponse instant for the upper bound. */
  botLagMilliseconds?: number;
}

/**
 * Best-effort instant when an Observation row was last materialized or clinically effective.
 *
 * @param observation - Flowsheet observation (typically the current non-cancelled row).
 * @returns Epoch milliseconds, or undefined if no usable timestamp is present.
 */
export function getObservationChangeInstant(observation: Observation): number | undefined {
  return (
    parseIsoInstant(observation.meta?.lastUpdated) ??
    parseIsoInstant(observation.issued) ??
    parseIsoInstant(observation.effectiveDateTime) ??
    parseIsoInstant(observation.effectivePeriod?.start)
  );
}

/**
 * When the flowsheet row was persisted (amend/sign), not the charted clinical time.
 * Used for encounter-amend diffs so backdated effective times still appear in the correct amend.
 */
export function getObservationAmendMaterializationInstant(
  observation: Observation,
): number | undefined {
  return parseIsoInstant(observation.meta?.lastUpdated) ?? parseIsoInstant(observation.issued);
}

/**
 * Whether an observation was materialized between two encounter QuestionnaireResponse saves.
 *
 * Observation `meta.versionId` and encounter QuestionnaireResponse `meta.versionId` are not
 * comparable across resource types; use change instants instead.
 */
function isObservationMaterializedWithinEncounterAmendmentInstantWindow(
  observation: Observation,
  previousEncounterQuestionnaireResponse: QuestionnaireResponse,
  currentEncounterQuestionnaireResponse: QuestionnaireResponse,
  botLagMilliseconds: number,
): boolean | undefined {
  const previousInstant = getQuestionnaireResponseChangeInstant(
    previousEncounterQuestionnaireResponse,
  );
  const currentInstant = getQuestionnaireResponseChangeInstant(
    currentEncounterQuestionnaireResponse,
  );
  const observationInstant = getObservationAmendMaterializationInstant(observation);

  if (
    previousInstant === undefined ||
    currentInstant === undefined ||
    observationInstant === undefined
  ) {
    return undefined;
  }

  const lower = Math.min(previousInstant, currentInstant);
  const upper = Math.max(previousInstant, currentInstant) + botLagMilliseconds;

  return observationInstant > lower && observationInstant <= upper;
}

/**
 * Returns whether an observation belongs to the encounter amend pair being viewed.
 *
 * Uses materialization time (`meta.lastUpdated` / `issued`) on the **leaf** observation row
 * between the previous and current encounter QuestionnaireResponse versions. Predecessors in
 * an `ObservationPreviousVersion` chain are not used for inclusion (only for diff endpoints).
 *
 * When encounter bounds exist but the observation has no materialization instant, returns false.
 * When encounter bounds are incomplete, returns true so diffs are not hidden due to missing metadata.
 */
export function isObservationWithinEncounterAmendmentWindow(
  observation: Observation,
  previousEncounterQuestionnaireResponse: QuestionnaireResponse,
  currentEncounterQuestionnaireResponse: QuestionnaireResponse,
  options: EncounterAmendmentObservationFilterOptions = {},
): boolean {
  const botLagMilliseconds = options.botLagMilliseconds ?? DEFAULT_ENCOUNTER_AMEND_BOT_LAG_MS;

  const materializationWindowResult =
    isObservationMaterializedWithinEncounterAmendmentInstantWindow(
      observation,
      previousEncounterQuestionnaireResponse,
      currentEncounterQuestionnaireResponse,
      botLagMilliseconds,
    );

  if (materializationWindowResult !== undefined) {
    return materializationWindowResult;
  }

  const previousInstant = getQuestionnaireResponseChangeInstant(
    previousEncounterQuestionnaireResponse,
  );
  const currentInstant = getQuestionnaireResponseChangeInstant(
    currentEncounterQuestionnaireResponse,
  );

  if (previousInstant === undefined || currentInstant === undefined) {
    return true;
  }

  return false;
}

function getLogicalFlowsheetRowKey(observation: Observation): string {
  const subjectReference = observation.subject?.reference ?? "";
  const encounterReference = observation.encounter?.reference ?? "";
  const formCode = observation.code?.coding?.[0]?.code ?? "";
  return `${formCode}|${observation.effectiveDateTime ?? ""}|${subjectReference}|${encounterReference}`;
}

function observationsShareDerivedFromQuestionnaireResponse(
  firstObservation: Observation,
  secondObservation: Observation,
): boolean {
  const questionnaireResponseId = getQuestionnaireResponseIdFromObservationDerivedFrom(
    firstObservation.derivedFrom,
  );
  if (!questionnaireResponseId) {
    return false;
  }
  return (
    getQuestionnaireResponseIdFromObservationDerivedFrom(secondObservation.derivedFrom) ===
    questionnaireResponseId
  );
}

/** Cancelled row superseded by a bot upsert during the same flowsheet QuestionnaireResponse amend. */
function isBotSupersededFlowsheetPredecessor(
  leafObservation: Observation,
  predecessorObservation: Observation,
): boolean {
  return (
    predecessorObservation.status === "cancelled" &&
    observationsShareDerivedFromQuestionnaireResponse(leafObservation, predecessorObservation)
  );
}

function hasPreAmendFlowsheetRowForSameSlot(
  observation: Observation,
  observationsById: ReadonlyMap<string, Observation> | undefined,
  beforeInstant: number,
): boolean {
  if (!observationsById) {
    return false;
  }

  const slotKey = getLogicalFlowsheetRowKey(observation);

  for (const candidate of observationsById.values()) {
    if (candidate.id === observation.id) {
      continue;
    }
    if (getLogicalFlowsheetRowKey(candidate) !== slotKey) {
      continue;
    }
    const candidateMaterializationInstant = getObservationAmendMaterializationInstant(candidate);
    if (
      candidateMaterializationInstant !== undefined &&
      candidateMaterializationInstant <= beforeInstant
    ) {
      return true;
    }
  }

  return false;
}

export interface EncounterAmendmentObservationDiffOptions
  extends EncounterAmendmentObservationFilterOptions {
  /** Encounter observations keyed by id; used to detect untouched stage members vs new rows. */
  observationsById?: ReadonlyMap<string, Observation>;
  /** Flowsheet QuestionnaireResponses keyed by id; used for ParentVersionId amend membership. */
  questionnaireResponsesById?: ReadonlyMap<string, QuestionnaireResponse>;
}

type EncounterAmendExtensionMembership = "include" | "exclude" | "unknown";

/**
 * When the flowsheet QR was stamped with QuestionnaireResponseParentVersionId, use that instead of
 * materialization time windows.
 */
function resolveObservationEncounterAmendExtensionMembership(
  observation: Observation,
  previousEncounterQuestionnaireResponse: QuestionnaireResponse,
  currentEncounterQuestionnaireResponse: QuestionnaireResponse,
  questionnaireResponsesById?: ReadonlyMap<string, QuestionnaireResponse>,
): EncounterAmendExtensionMembership {
  const currentEncounterVersionId = currentEncounterQuestionnaireResponse.meta?.versionId;
  const previousEncounterVersionId = previousEncounterQuestionnaireResponse.meta?.versionId;

  const observationParentVersionId = getObservationEncounterAmendParentVersionId(observation);
  if (observationParentVersionId && currentEncounterVersionId) {
    if (observationParentVersionId === currentEncounterVersionId) {
      return "include";
    }
    if (previousEncounterVersionId && observationParentVersionId === previousEncounterVersionId) {
      return "exclude";
    }
    return "exclude";
  }

  const questionnaireResponseId = getQuestionnaireResponseIdFromObservationDerivedFrom(
    observation.derivedFrom,
  );

  if (!questionnaireResponseId || !currentEncounterVersionId || !questionnaireResponsesById) {
    return "unknown";
  }

  const flowsheetQuestionnaireResponse = questionnaireResponsesById.get(questionnaireResponseId);
  const parentVersionId = getQuestionnaireResponseEncounterParentVersionId(
    flowsheetQuestionnaireResponse,
  );

  if (!parentVersionId) {
    return "unknown";
  }

  if (parentVersionId === currentEncounterVersionId) {
    return "include";
  }

  if (previousEncounterVersionId && parentVersionId === previousEncounterVersionId) {
    return "exclude";
  }

  return "exclude";
}

/**
 * Whether a flowsheet observation should appear on the encounter version diff for one amend.
 *
 * Requires materialization between the previous and current encounter QuestionnaireResponse saves.
 * Unversioned rows (no `ObservationPreviousVersion` link) are excluded when an older row already
 * occupied the same logical slot before the amend, so Sign & Lock timestamp bumps do not list
 * every stage member.
 *
 * When the previous encounter QuestionnaireResponse has no change instant, slot-dedup cannot run;
 * returns true to match {@link isObservationWithinEncounterAmendmentWindow} permissive behavior.
 */
export function isObservationPartOfEncounterAmendmentDiff(
  observation: Observation,
  previousEncounterQuestionnaireResponse: QuestionnaireResponse,
  currentEncounterQuestionnaireResponse: QuestionnaireResponse,
  options: EncounterAmendmentObservationDiffOptions = {},
): boolean {
  const extensionMembership = resolveObservationEncounterAmendExtensionMembership(
    observation,
    previousEncounterQuestionnaireResponse,
    currentEncounterQuestionnaireResponse,
    options.questionnaireResponsesById,
  );

  if (extensionMembership === "include") {
    return true;
  }

  if (extensionMembership === "exclude") {
    return false;
  }

  if (
    !isObservationWithinEncounterAmendmentWindow(
      observation,
      previousEncounterQuestionnaireResponse,
      currentEncounterQuestionnaireResponse,
      options,
    )
  ) {
    return false;
  }

  const previousEncounterInstant = getQuestionnaireResponseChangeInstant(
    previousEncounterQuestionnaireResponse,
  );
  if (previousEncounterInstant === undefined) {
    return true;
  }

  const allObservations = options.observationsById ? [...options.observationsById.values()] : [];
  const previousVersionTargetId = resolveObservationAmendPredecessorId(
    observation,
    allObservations,
  );

  if (previousVersionTargetId) {
    const predecessor =
      options.observationsById?.get(previousVersionTargetId) ??
      allObservations.find((candidate) => candidate.id === previousVersionTargetId);
    const leafMaterializationInstant = getObservationAmendMaterializationInstant(observation);
    if (!predecessor || leafMaterializationInstant === undefined) {
      return false;
    }
    const predecessorMaterializationInstant =
      getObservationAmendMaterializationInstant(predecessor);
    if (predecessorMaterializationInstant === undefined) {
      return false;
    }

    // Bot cancel/create during amend updates the cancelled row after the amend baseline instant.
    if (isBotSupersededFlowsheetPredecessor(observation, predecessor)) {
      return leafMaterializationInstant > previousEncounterInstant;
    }

    // Extension-linked predecessor must predate the amend baseline (excludes re-versioned Sign & Lock).
    return (
      leafMaterializationInstant > previousEncounterInstant &&
      predecessorMaterializationInstant <= previousEncounterInstant
    );
  }

  const chartedInstant =
    parseIsoInstant(observation.effectiveDateTime) ?? parseIsoInstant(observation.issued);
  if (chartedInstant !== undefined && chartedInstant <= previousEncounterInstant) {
    return false;
  }

  return !hasPreAmendFlowsheetRowForSameSlot(
    observation,
    options.observationsById,
    previousEncounterInstant,
  );
}

/**
 * Whether an observation row was superseded during an amend (has a previous-version link).
 * Used when encounter QuestionnaireResponse history has no prior version to pair against.
 */
export function isObservationWithAmendVersionChain(
  observation: Observation,
  observationsById?: ReadonlyMap<string, Observation>,
): boolean {
  if (getObservationPreviousVersionTargetId(observation) !== undefined) {
    return true;
  }
  if (!observationsById) {
    return false;
  }
  return (
    resolveObservationAmendPredecessorId(observation, [...observationsById.values()]) !== undefined
  );
}
